/**
 * Hook: useDebounce
 * Purpose: 入力値を指定ms遅延させて返すカスタムフック
 * Re-generatable: Yes
 * Dependencies: react
 */

import { useEffect, useState } from 'react';

/**
 * 入力値をデバウンスする
 *
 * @param value - デバウンス対象の値
 * @param delay - 遅延時間（ミリ秒）デフォルト: 300ms
 * @returns デバウンスされた値
 *
 * @example
 * ```tsx
 * function SearchComponent() {
 *   const [searchQuery, setSearchQuery] = useState('');
 *   const debouncedQuery = useDebounce(searchQuery, 300);
 *
 *   useEffect(() => {
 *     // debouncedQueryが変更されたら検索実行
 *     performSearch(debouncedQuery);
 *   }, [debouncedQuery]);
 *
 *   return (
 *     <input
 *       value={searchQuery}
 *       onChange={(e) => setSearchQuery(e.target.value)}
 *     />
 *   );
 * }
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // 指定時間後に値を更新
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // クリーンアップ: 値が変更されたらタイマーをキャンセル
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
